/**
 * ShapeThumbnail - A simple Lit component for rendering shape compositions
 * as SVG thumbnails at any required size.
 *
 * This is a render-only component with no editing capabilities.
 * Use <ptc-editor> for interactive editing.
 *
 * @example
 * ```html
 * <ptc-thumbnail
 *   .composition=${myComposition}
 *   width="200"
 *   height="150"
 *   fit="contain"
 * ></ptc-thumbnail>
 * ```
 */

import shapethumbnailStyles from './ShapeThumbnail.styles.css?inline';
import { LitElement, html, PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { ShapeComposition, RenderOptions } from '../types';
import { ShapeRenderer } from '../renderer/ShapeRenderer';
import { AssetManager } from '../models/AssetManager';
import { expandGrid, type GridComposition } from '../models/GridModel';

export type FitMode = 'contain' | 'cover' | 'fill' | 'none';

/**
 * A lightweight component for displaying shape compositions as SVG thumbnails.
 *
 * Features:
 * - Renders at any size with configurable fit mode
 * - Supports async asset loading
 * - Optional label display
 * - CSS styling via custom properties
 *
 * @fires render-complete - When SVG rendering completes
 * @fires render-error - When rendering fails
 *
 * @csspart container - The outer container div
 * @csspart svg - The rendered SVG element
 *
 * @cssprop --thumbnail-background - Background color (default: transparent)
 * @cssprop --thumbnail-border - Border style (default: none)
 * @cssprop --thumbnail-border-radius - Border radius (default: 0)
 */
@customElement('ptc-thumbnail')
export class ShapeThumbnail extends LitElement {
  static styles = unsafeCSS(shapethumbnailStyles);

  /**
   * The shape composition to render.
   */
  @property({ type: Object })
  composition: ShapeComposition | null = null;

  /**
   * Authored grid composition to render. When set, it is expanded to the
   * internal composition and takes precedence over `composition`.
   */
  @property({ type: Object })
  grid: GridComposition | null = null;

  /**
   * Width of the thumbnail in pixels.
   * If not set, uses natural composition width.
   */
  @property({ type: Number })
  width?: number;

  /**
   * Height of the thumbnail in pixels.
   * If not set, uses natural composition height.
   */
  @property({ type: Number })
  height?: number;

  /**
   * How the SVG should fit within the thumbnail bounds.
   * - contain: Scale to fit, preserve aspect ratio
   * - cover: Scale to cover, may clip
   * - fill: Stretch to fill
   * - none: Use natural size
   */
  @property({ type: String })
  fit: FitMode = 'contain';

  /**
   * Whether to draw the opening / handing labels (OL, OR, L, R, F, CWL, ...)
   * that each applied asset declares in the manifest.
   *
   * On by default: the label is part of the standard drawing, and for assets
   * carrying no geometry (fixed light, curtain wall, window wall, roof window)
   * it is the only thing identifying the opening. This default also governs the
   * persisted configuration SVG, which is produced through this component.
   */
  @property({ type: Boolean, attribute: 'show-labels' })
  showLabels = true;

  /**
   * Whether to show opening boundaries.
   */
  @property({ type: Boolean, attribute: 'show-boundaries' })
  showBoundaries = true;

  /**
   * Whether to draw the dimension overlay (overall + per-track sizes with lock
   * indicators). Off for plain thumbnails; the AS 2047 save path turns it on so
   * the exported configuration SVG is a dimensioned drawing.
   */
  @property({ type: Boolean, attribute: 'show-dimensions' })
  showDimensions = false;

  /**
   * Render scale (pixels per mm).
   */
  @property({ type: Number })
  scale = 0.5;

  /**
   * Background color for the SVG.
   */
  @property({ type: String, attribute: 'background-color' })
  backgroundColor = 'transparent';

  /**
   * Asset manager for loading SVG assets.
   */
  @property({ type: Object, attribute: false })
  assetManager: AssetManager | null = null;

  @state()
  private loading = false;

  @state()
  private error: string | null = null;

  @state()
  private svgContent: string | null = null;

  private renderer: ShapeRenderer | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.renderer = new ShapeRenderer();
  }

  updated(changedProps: PropertyValues): void {
    if (
      changedProps.has('composition') ||
      changedProps.has('grid') ||
      changedProps.has('scale') ||
      changedProps.has('showLabels') ||
      changedProps.has('showBoundaries') ||
      changedProps.has('showDimensions') ||
      changedProps.has('backgroundColor') ||
      changedProps.has('assetManager')
    ) {
      void this.doRender();
    }
  }

  /**
   * The composition actually rendered: the expanded grid when `grid` is set,
   * otherwise the directly-supplied `composition`.
   */
  private resolveComposition(): ShapeComposition | null {
    if (this.grid) return expandGrid(this.grid).composition;
    return this.composition;
  }

  private async doRender(): Promise<void> {
    const composition = this.resolveComposition();
    if (!composition || !this.renderer) {
      this.svgContent = null;
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      if (this.assetManager) {
        this.renderer.setAssetManager(this.assetManager);
      }

      const options: RenderOptions = {
        scale: this.scale,
        showLabels: this.showLabels,
        showBoundaries: this.showBoundaries,
        showDimensions: this.showDimensions,
        // A thumbnail / exported drawing shows plain dimensions only - no lock
        // accent or padlocks (those are an editor authoring aid).
        lockIndicators: false,
        backgroundColor: this.backgroundColor,
        // Room for the outer dimension tiers: the tier line sits 40 units out
        // from the frame and its label is drawn beyond that, rotated on the
        // vertical side - so the reserve must clear 40 plus a full line height,
        // or the outermost text is clipped by the viewBox.
        padding: this.showDimensions ? 80 : 0,
      };

      const result = await this.renderer.render(composition, options);
      this.svgContent = result.svg;

      this.dispatchEvent(
        new CustomEvent('render-complete', {
          detail: { result },
          bubbles: true,
          composed: true,
        })
      );
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Render failed';
      this.dispatchEvent(
        new CustomEvent('render-error', {
          detail: { error: this.error },
          bubbles: true,
          composed: true,
        })
      );
    } finally {
      this.loading = false;
    }
  }

  render() {
    const containerStyles: string[] = [];
    if (this.width) containerStyles.push(`width: ${this.width}px`);
    if (this.height) containerStyles.push(`height: ${this.height}px`);

    return html`
      <div
        class="container fit-${this.fit} ${this.loading ? 'loading' : ''}"
        style=${containerStyles.join('; ')}
        part="container"
      >
        ${
          this.error
            ? html`<div class="error">${this.error}</div>`
            : this.svgContent
              ? html`<div class="svg-wrapper" part="svg">
                  ${unsafeHTML(this.svgContent)}
                </div>`
              : html`<div class="placeholder">No composition</div>`
        }
      </div>
    `;
  }

  /**
   * Force a re-render of the thumbnail.
   */
  async refresh(): Promise<void> {
    await this.doRender();
  }

  /**
   * Get the current SVG content as a string.
   */
  getSvgContent(): string | null {
    return this.svgContent;
  }

  /**
   * Download the SVG as a file.
   */
  downloadSvg(filename?: string): void {
    const composition = this.resolveComposition();
    if (!this.svgContent || !composition) return;

    const name =
      filename ?? `${composition.name.replace(/\s+/g, '-').toLowerCase()}.svg`;
    const blob = new Blob([this.svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Copy the SVG content to clipboard.
   */
  async copyToClipboard(): Promise<boolean> {
    if (!this.svgContent) return false;

    try {
      await navigator.clipboard.writeText(this.svgContent);
      return true;
    } catch {
      return false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ptc-thumbnail': ShapeThumbnail;
  }
}
